import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import MissionItem from "./MissionItem";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import { makeStyles } from "tss-react/mui";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectMissionsRestSet,
  selectSelectedRoom,
  selectSelectedRoomMissions,
} from "../../../store/room/room-selectors";
import { selectCurrentRobotState, selectIsCleaningActionEnabled } from "../../../store/status/status-selectors";
import Mission from "../../../interfaces/robot/missions/mission";
import { useSearchParams } from "react-router-dom";
import { FilterKeys } from "../../../enums/query-params/filters";
import MissionForm from "./MissionForm";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import {
  errorResetResponse,
  isErrorSetOpen,
  isSuccessSetOpen,
  successResetResponse,
} from "../../../interfaces/api/rest-set";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import { AddIcon, CrossCloseIcon } from "../../../utils/icons/icons";
import ZoomTransformWrapper from "../ZonePlanner/ZoomTransformWrapper";
import { ReactZoomPanPinchContentRef } from "react-zoom-pan-pinch";
import ZonePlannerMapZones from "../ZonePlanner/ZonePlannerMapZones";
import Base64Image from "../../misc/Base64Image";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";

const useStyles = makeStyles()((theme) => ({
  scrollbar: {
    "&::-webkit-scrollbar": {
      width: theme.spacing(1.8),
    },
    "&::-webkit-scrollbar-track": {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[100],
    },
    "&::-webkit-scrollbar-thumb": {
      borderLeft: `${theme.spacing(1)} solid rgba(0, 0, 0, 0)`,
      backgroundClip: "padding-box",
      backgroundColor: theme.palette.grey[300],
    },
  },
}));

const MissionsGrid: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const [queryParams, setQueryParams] = useSearchParams();
  const queryMissionId = queryParams.get(FilterKeys.MissionId);
  const matchesLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const dispatch = useSharedDispatch();

  const { patchMissionsRestSet, setZonePlannerMouseState } = useSharedActions();

  const selectedRoom = useSharedSelector((state) => selectSelectedRoom(state));
  const missions = useSharedSelector((state) => selectSelectedRoomMissions(state));
  const isCleaningActionEnabled = useSharedSelector((state) => selectIsCleaningActionEnabled(state));
  const currentRobotState = useSharedSelector((state) => selectCurrentRobotState(state));
  const missionsRestSet = useSharedSelector((state) => selectMissionsRestSet(state));

  const [editingMission, setEditingMission] = useState<Partial<Mission> | null>(
    queryMissionId != null ? missions.find((m) => m.id === queryMissionId) ?? { id: queryMissionId } : null
  );
  const [hoveredIds, setHoveredIds] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);

  const transformRef = useRef<ReactZoomPanPinchContentRef | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(setZonePlannerMouseState(ZoneControllerMouseState.Default));
  }, []);

  useLayoutEffect(() => {
    if (transformRef.current && selectedRoom?.mapInformation && isInitialized) {
      const { instance } = transformRef.current;

      const containerWidth = instance.wrapperComponent?.clientWidth || 1;
      const containerHeight = instance.wrapperComponent?.clientHeight || 1;

      const svgWidth = selectedRoom.mapInformation.width;
      const svgHeight = selectedRoom.mapInformation.height;

      const scale = Math.min(containerWidth / svgWidth, containerHeight / svgHeight);

      instance.setTransformState(
        scale,
        (containerWidth - svgWidth * scale) / 2,
        (containerHeight - svgHeight * scale) / 2
      );
    }
  }, [selectedRoom?.mapInformation, isInitialized, theme]);

  const openMissionFormHandler = (mission: Partial<Mission>) => {
    setEditingMission(mission);
  };

  const closeMissionFormHandler = () => {
    setEditingMission(null);
    setQueryParams((prevState) => {
      prevState.delete(FilterKeys.MissionId);
      return prevState;
    });
  };

  const missionHoverHandler = (ids: string[]) => {
    setHoveredIds(ids);
  };

  const closeMissionMapDialogHandler = () => {
    setHoveredIds([]);
  };

  const resetMissionsStateHandler = () => {
    dispatch(patchMissionsRestSet(successResetResponse(missionsRestSet)));
  };

  const resetMissionsErrorStateHandler = () => {
    dispatch(patchMissionsRestSet(errorResetResponse(missionsRestSet)));
  };

  const zoomChangeHandler = (scale: number) => {
    setZoomScale(scale);
  };

  const missionsMap =
    selectedRoom == null ? null : (
      <div style={{ height: "100%", position: "relative", width: "100%" }} ref={mapContainerRef}>
        <ZoomTransformWrapper
          onZoomChange={zoomChangeHandler}
          ref={(node) => {
            transformRef.current = node;
            setIsInitialized(true);
          }}
          initialScale={zoomScale}
        >
          <Box>
            <ZonePlannerMapZones
              roomData={selectedRoom}
              zoomScale={zoomScale ?? 1}
              missionView={{
                hoveredIds,
              }}
            />
            <Base64Image imageString={selectedRoom.map} />
          </Box>
        </ZoomTransformWrapper>
      </div>
    );

  return (
    <React.Fragment>
      <Grid container sx={{ height: "100%", overflow: "hidden" }} direction="row">
        <Grid item xs={12} lg={7} p={1} className={classes.scrollbar} sx={{ height: "100%", overflowY: "auto" }}>
          {selectedRoom?.secondaryStation != null ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("warning-cannot-create-mission-for-transition-room")}
            </Alert>
          ) : (
            <React.Fragment>
              <ReadOnlyProtectedButton
                sx={{ mb: 1 }}
                onClick={() => openMissionFormHandler({})}
                startIcon={<AddIcon />}
              >
                {t("button-create-mission")}
              </ReadOnlyProtectedButton>
              {missions.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("info-no-missions-for-room")}
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {missions
                    .sort((a, b) => {
                      return a.name > b.name ? 1 : -1;
                    })
                    .map((mission) => (
                      <MissionItem
                        key={mission.id}
                        mission={mission}
                        cleaningActionEnabled={isCleaningActionEnabled}
                        onEdit={openMissionFormHandler}
                        onHover={missionHoverHandler}
                        wasAborted={currentRobotState?.mission?.abortedMissionId === mission.id}
                      />
                    ))}
                </Stack>
              )}
            </React.Fragment>
          )}
        </Grid>
        {matchesLgUp && selectedRoom != null && (
          <Grid item lg={5} sx={{ height: "100%" }}>
            {missionsMap}
          </Grid>
        )}
      </Grid>
      {editingMission != null && selectedRoom?.id != null && (
        <MissionForm
          open={setEditingMission != null}
          onClose={closeMissionFormHandler}
          roomId={selectedRoom.id}
          mission={editingMission}
        />
      )}
      <Dialog
        open={!matchesLgUp && hoveredIds.length > 0}
        onClose={closeMissionMapDialogHandler}
        fullWidth
        maxWidth="md"
      >
        <DialogContent sx={(theme) => ({ height: `calc(100vh - ${theme.spacing(8)})` })}>
          <Box
            sx={(theme) => ({
              position: "absolute",
              right: theme.spacing(1),
              top: theme.spacing(1),
              zIndex: theme.zIndex.fab,
            })}
          >
            <IconButton onClick={closeMissionMapDialogHandler}>
              <CrossCloseIcon />
            </IconButton>
          </Box>
          {missionsMap}
        </DialogContent>
      </Dialog>
      <CustomSnackbar
        title={
          missionsRestSet.deleteStatus === HttpFetchStatus.Failed
            ? t("error-deleting-mission")
            : t("error-saving-mission")
        }
        textMessages={getSaveErrorMessages(
          missionsRestSet.deleteError ?? missionsRestSet.patchError ?? missionsRestSet.postError,
        )}
        open={isErrorSetOpen(missionsRestSet)}
        severity="error"
        onClose={resetMissionsErrorStateHandler}
      />
      <CustomSnackbar
        title={
          missionsRestSet.deleteStatus === HttpFetchStatus.Success
            ? t("success-deleting-mission")
            : t("success-saving-mission")
        }
        open={isSuccessSetOpen(missionsRestSet)}
        severity="success"
        onClose={resetMissionsStateHandler}
      />
    </React.Fragment>
  );
};

export default MissionsGrid;
